import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { 
  CheckCircle, 
  Lock, 
  Play, 
  Award, 
  Target, 
  BookOpen,
  TrendingUp,
  Clock,
  ChevronRight,
  Flame,
  Trophy,
  Zap,
  ArrowRight
} from 'lucide-react';
import { curriculum } from '../data/mock';
import { moduleContent } from '../data/moduleContent';
import { getUserProgress, getScenarioStats, getUserLessonProgress, getEnrollmentInfo } from '../services/api';
import CertificateCountdown from './CertificateCountdown';

const ProgressDashboard = ({ leadId, userName }) => {
  const [progress, setProgress] = useState([]);
  const [lessonProgress, setLessonProgress] = useState([]);
  const [scenarioStats, setScenarioStats] = useState(null);
  const [enrollmentInfo, setEnrollmentInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProgress = async () => {
      if (!leadId) {
        setIsLoading(false);
        return;
      }
      
      try {
        const [progressData, statsData, lessonData, enrollData] = await Promise.all([
          getUserProgress(leadId),
          getScenarioStats(leadId),
          getUserLessonProgress(leadId).catch(() => []),
          getEnrollmentInfo(leadId).catch(() => null)
        ]);
        setProgress(progressData);
        setScenarioStats(statsData);
        setLessonProgress(lessonData || []);
        setEnrollmentInfo(enrollData);
      } catch (error) {
        console.error('Error fetching progress:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProgress();
  }, [leadId]);

  // Calculate total lessons across all modules
  const getTotalLessons = () => {
    return Object.values(moduleContent).reduce((sum, mod) => sum + (mod.lessons?.length || 0), 0);
  };

  // Calculate completed lessons
  const getCompletedLessons = () => {
    return lessonProgress.length;
  };

  // Get lessons completed for a specific module
  const getModuleLessonCount = (moduleId) => {
    const mod = moduleContent[moduleId];
    return mod?.lessons?.length || 0;
  };

  const getModuleLessonsCompleted = (moduleId) => {
    return lessonProgress.filter(lp => lp.module_id === moduleId).length;
  };

  // Find next recommended lesson
  const getNextLesson = () => {
    for (let moduleId = 1; moduleId <= 8; moduleId++) {
      const mod = moduleContent[moduleId];
      if (!mod?.lessons) continue;
      
      for (let lesson of mod.lessons) {
        const isCompleted = lessonProgress.some(
          lp => lp.module_id === moduleId && lp.lesson_id === lesson.id
        );
        if (!isCompleted) {
          return { moduleId, lessonId: lesson.id, lessonTitle: lesson.title, moduleTitle: mod.title };
        }
      }
    }
    return null;
  };

  // Calculate estimated time remaining
  const getEstimatedTimeRemaining = () => {
    const completedLessons = getCompletedLessons();
    const totalLessons = getTotalLessons();
    const remainingLessons = totalLessons - completedLessons;
    
    // Average 45 min per lesson
    const avgMinutesPerLesson = 45;
    const totalMinutes = remainingLessons * avgMinutesPerLesson;
    
    if (totalMinutes < 60) return `${totalMinutes} minutes`;
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours} hours`;
  };

  const completedModules = progress.filter(p => p.completed).length;
  const totalModules = curriculum.length;
  const totalLessons = getTotalLessons();
  const completedLessons = getCompletedLessons();
  const overallProgress = totalLessons > 0 ? (completedLessons / totalLessons) * 100 : 0;
  const nextLesson = getNextLesson();

  const getModuleProgress = (moduleId) => {
    return progress.find(p => p.module_id === moduleId);
  };

  // Calculate streak (mock for now)
  const streak = Math.min(completedLessons, 7);

  if (isLoading) {
    return (
      <section className="py-12 bg-emerald-50">
        <div className="container mx-auto px-6">
          <div className="animate-pulse flex flex-col items-center">
            <div className="h-8 bg-emerald-200 rounded w-48 mb-4"></div>
            <div className="h-4 bg-emerald-200 rounded w-64"></div>
          </div>
        </div>
      </section>
    );
  }

  if (!leadId) return null;

  return (
    <section className="py-12 bg-gradient-to-b from-emerald-50 to-white" id="progress">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
            Welcome back{userName ? `, ${userName.split(' ')[0]}` : ''}! 👋
          </h2>
          <p className="text-slate-600">Track your progress and continue learning</p>
        </div>

        {/* Hero Stats Row */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto mb-8">
          {/* Overall Progress Card */}
          <Card className="p-5 bg-gradient-to-br from-emerald-500 to-teal-600 text-white col-span-1 lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-emerald-100 text-sm">Overall Progress</p>
                <p className="text-3xl font-bold">{Math.round(overallProgress)}%</p>
              </div>
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
            </div>
            <Progress value={overallProgress} className="h-2 bg-white/20" />
            <div className="flex justify-between mt-2 text-xs text-emerald-100">
              <span>{completedLessons} of {totalLessons} lessons complete</span>
              <span>{getEstimatedTimeRemaining()} remaining</span>
            </div>
          </Card>

          {/* Streak Card */}
          <Card className="p-5 bg-white border-orange-200 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <Flame className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Learning Streak</p>
                <p className="text-2xl font-bold text-slate-900">{streak} days</p>
              </div>
            </div>
            <p className="text-xs text-slate-500">Keep it going! 🔥</p>
          </Card>

          {/* Certificate Status */}
          <Card className="p-5 bg-white border-purple-200 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                overallProgress >= 100 ? 'bg-purple-100' : 'bg-slate-100'
              }`}>
                <Award className={`w-5 h-5 ${overallProgress >= 100 ? 'text-purple-500' : 'text-slate-400'}`} />
              </div>
              <div>
                <p className="text-xs text-slate-500">Certificate</p>
                <p className="text-lg font-bold text-slate-900">
                  {overallProgress >= 100 ? 'Ready!' : `${Math.round(100 - overallProgress)}% to go`}
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              {overallProgress >= 100 ? 'Claim your certificate below' : 'Complete all modules to earn'}
            </p>
          </Card>
        </div>

        {/* Certificate Countdown Timer */}
        {enrollmentInfo && overallProgress < 100 && (
          <div className="max-w-5xl mx-auto mb-8">
            <CertificateCountdown 
              enrollmentDate={enrollmentInfo.enrollment_date}
              daysAllowed={enrollmentInfo.days_allowed || 30}
              overallProgress={overallProgress}
            />
          </div>
        )}

        {/* Next Up Card */}
        {nextLesson && (
          <Card className="max-w-5xl mx-auto mb-8 p-6 bg-gradient-to-r from-slate-900 to-slate-800 text-white overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative">
              <div className="flex items-center gap-2 text-emerald-400 mb-2">
                <Zap className="w-4 h-4" />
                <span className="text-sm font-medium">Continue Where You Left Off</span>
              </div>
              <h3 className="text-xl font-bold mb-1">{nextLesson.lessonTitle}</h3>
              <p className="text-slate-400 text-sm mb-4">Module {nextLesson.moduleId}: {nextLesson.moduleTitle}</p>
              <Link to={`/module/${nextLesson.moduleId}`}>
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  Continue Learning
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </Card>
        )}

        {/* Module Roadmap */}
        <Card className="max-w-5xl mx-auto p-6 bg-white">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-emerald-500" />
              Your Learning Journey
            </h3>
            <span className="text-sm text-slate-500">
              {completedModules} of {totalModules} modules started
            </span>
          </div>
          
          {/* Visual Roadmap */}
          <div className="relative mb-8">
            {/* Progress line */}
            <div className="absolute top-5 left-5 right-5 h-1 bg-slate-200 rounded-full">
              <div 
                className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                style={{ width: `${(completedModules / totalModules) * 100}%` }}
              />
            </div>
            
            {/* Module dots */}
            <div className="relative flex justify-between">
              {curriculum.map((module, index) => {
                const moduleLessons = getModuleLessonCount(module.id);
                const moduleLessonsCompleted = getModuleLessonsCompleted(module.id);
                const moduleProgressPct = moduleLessons > 0 ? (moduleLessonsCompleted / moduleLessons) * 100 : 0;
                const isCompleted = moduleProgressPct >= 100;
                const isInProgress = moduleProgressPct > 0 && moduleProgressPct < 100;
                
                return (
                  <div key={module.id} className="flex flex-col items-center" style={{ width: '12.5%' }}>
                    <Link to={`/module/${module.id}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                        isCompleted 
                          ? 'bg-emerald-500 border-emerald-500 text-white' 
                          : isInProgress
                            ? 'bg-emerald-100 border-emerald-500 text-emerald-600'
                            : 'bg-white border-slate-300 text-slate-400'
                      }`}>
                        {isCompleted ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <span className="font-semibold text-sm">{index + 1}</span>
                        )}
                      </div>
                    </Link>
                    <p className="text-xs text-slate-500 mt-2 text-center hidden md:block max-w-[80px] truncate">
                      {module.title.split(' ').slice(0, 2).join(' ')}
                    </p>
                    {isInProgress && (
                      <span className="text-xs text-emerald-600 font-medium mt-1">
                        {Math.round(moduleProgressPct)}%
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Detailed Module List */}
          <div className="space-y-3">
            {curriculum.map((module, index) => {
              const moduleLessons = getModuleLessonCount(module.id);
              const moduleLessonsCompleted = getModuleLessonsCompleted(module.id);
              const moduleProgressPct = moduleLessons > 0 ? (moduleLessonsCompleted / moduleLessons) * 100 : 0;
              const isCompleted = moduleProgressPct >= 100;
              const isInProgress = moduleProgressPct > 0 && moduleProgressPct < 100;
              const moduleProgressData = getModuleProgress(module.id);
              
              return (
                <Link 
                  key={module.id}
                  to={`/module/${module.id}`}
                  className="block"
                >
                  <div 
                    className={`flex items-center gap-4 p-4 rounded-xl transition-all hover:shadow-md ${
                      isCompleted ? 'bg-emerald-50 border border-emerald-200' : 
                      isInProgress ? 'bg-blue-50 border border-blue-200' : 
                      'bg-slate-50 border border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      isCompleted ? 'bg-emerald-500' : 
                      isInProgress ? 'bg-blue-500' : 'bg-slate-400'
                    }`}>
                      {isCompleted ? (
                        <Trophy className="w-6 h-6 text-white" />
                      ) : (
                        <span className="text-white font-bold">{index + 1}</span>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-slate-900 truncate">
                          {module.title}
                        </h4>
                        {isCompleted && (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                            Complete
                          </span>
                        )}
                        {isInProgress && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                            In Progress
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {module.duration}
                        </span>
                        <span className="text-xs text-slate-500">
                          {moduleLessonsCompleted}/{moduleLessons} lessons
                        </span>
                        {moduleProgressData?.quiz_score !== undefined && (
                          <span className="text-xs text-emerald-600 font-medium">
                            Quiz: {moduleProgressData.quiz_score}%
                          </span>
                        )}
                      </div>
                      {isInProgress && (
                        <div className="mt-2">
                          <Progress value={moduleProgressPct} className="h-1.5" />
                        </div>
                      )}
                    </div>
                    
                    <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  </div>
                </Link>
              );
            })}
          </div>
        </Card>

        {/* Achievement/Certificate Section */}
        {overallProgress >= 100 && (
          <Card className="max-w-5xl mx-auto mt-8 p-8 bg-gradient-to-br from-purple-600 to-indigo-700 text-white text-center">
            <Trophy className="w-16 h-16 mx-auto mb-4 text-yellow-300" />
            <h3 className="text-2xl font-bold mb-2">Congratulations! 🎉</h3>
            <p className="text-purple-200 mb-6">You&apos;ve completed all modules. Claim your certificate now!</p>
            <Button className="bg-white text-purple-600 hover:bg-purple-50" size="lg">
              <Award className="w-5 h-5 mr-2" />
              Get Your Certificate
            </Button>
          </Card>
        )}
      </div>
    </section>
  );
};

export default ProgressDashboard;
